/**
 * The mark, drawn rather than imported.
 *
 * ## What it is
 *
 * A square pyramid — `hình chóp S.ABCD`, the figure every Vietnamese solid-geometry course is
 * built around — in the **same cabinet projection the application draws in**: depth axis at 60°,
 * foreshortened by half. The coordinates below are that projection applied to a unit pyramid,
 * not a shape that merely looks geometric.
 *
 * Two corners of the base are behind the body from this angle, so the three edges meeting there
 * are **dashed**. That is the whole identity. A dashed hidden edge is a convention every maths
 * teacher in the country reads instantly, it belongs to this subject and no technology brand has
 * taken it, and it says the true thing about the work: the visible line is what you see, the
 * dashed one is what you understand.
 *
 * Orange belongs to *points* and to nothing else — the apex here, and the tittle of the `i` in
 * the wordmark. One colour used in one way is a signature; used everywhere it is decoration.
 *
 * ## Why the small size is a different drawing
 *
 * Below about 32px an eight-edge figure collapses into a grey smudge, and the dashes are the
 * first thing to go — a dash and a gap at that size are one pixel each. So the mark changes
 * state rather than merely scaling: it stops being a *drawing* and becomes a **solid**, two flat
 * faces in two tones. It keeps the silhouette and the orange apex, which is what makes it
 * findable among a row of tabs.
 *
 * That is a real distinction rather than a convenience. At reading size the figure is notation;
 * at icon size it is an object.
 */

export interface LogoProps {
  /** Height in pixels. Width follows the mark's own proportion. */
  readonly size?: number;
  /**
   * `drawn` is the line figure with its dashes; `solid` is the two-tone block for small sizes.
   * Defaults by size, which is the rule rather than a preference — see above.
   */
  readonly variant?: 'drawn' | 'solid';
  /** Inverts for a coloured or dark ground. */
  readonly onDark?: boolean;
}

/** The projected pyramid, in the mark's own coordinate space. */
const S = { x: 62.5, y: 0 };
const A = { x: 0, y: 131.6 };
const B = { x: 100, y: 131.6 };
const C = { x: 125, y: 88.4 };
const D = { x: 25, y: 88.4 };

/** A corner as SVG writes it, in the two shapes the format asks for. */
const at = (p: { x: number; y: number }): string => `${String(p.x)} ${String(p.y)}`;
const pair = (p: { x: number; y: number }): string => `${String(p.x)},${String(p.y)}`;

/** Everything in front: the near base edges and the three lateral edges that are not behind. */
const VISIBLE = `M${at(A)} H${String(B.x)} L${at(C)} M${at(A)} L${at(S)} L${at(C)} M${at(B)} L${at(S)}`;
/** The corner the body hides, and the three edges that meet there. */
const HIDDEN = `M${at(A)} L${at(D)} H${String(C.x)} M${at(D)} L${at(S)}`;

/** Below this the dashes stop being legible and the solid variant takes over. */
export const SOLID_BELOW = 32;

export function Logo({ size = 40, variant, onDark = false }: LogoProps) {
  const drawn = (variant ?? (size < SOLID_BELOW ? 'solid' : 'drawn')) === 'drawn';
  const width = Math.round(size * (145 / 152));

  const line = onDark ? '#ffffff' : 'var(--es-accent)';
  const faint = onDark ? 'rgb(255 255 255 / 55%)' : 'var(--es-ink-400)';
  const face = onDark ? '#ffffff' : 'var(--es-accent)';
  const side = onDark ? 'rgb(255 255 255 / 55%)' : 'var(--es-accent-ink)';

  return (
    <svg
      width={width}
      height={size}
      viewBox="-10 -10 145 152"
      role="img"
      aria-label="hinhhoc.vn"
      style={{ display: 'block', flex: 'none' }}
    >
      {drawn ? (
        <>
          <path
            d={VISIBLE}
            fill="none"
            stroke={line}
            strokeWidth={8}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d={HIDDEN}
            fill="none"
            stroke={faint}
            strokeWidth={5.5}
            strokeLinecap="round"
            strokeDasharray="11 9"
          />
        </>
      ) : (
        <>
          <polygon points={`${pair(A)} ${pair(S)} ${pair(B)}`} fill={face} />
          <polygon points={`${pair(B)} ${pair(S)} ${pair(C)}`} fill={side} />
        </>
      )}
      <circle cx={S.x} cy={drawn ? S.y : 6} r={drawn ? 11 : 15} fill="var(--es-apex)" />
    </svg>
  );
}

export interface WordmarkProps {
  readonly size?: number;
  readonly onDark?: boolean;
}

/**
 * `hinhhoc.vn`, set rather than drawn.
 *
 * The name has exactly one `i`, so the orange tittle appears exactly once — the same point that
 * sits at the apex of the mark. `.vn` drops to regular weight and to the muted ink: it is the
 * address, not the name, and setting both at one weight makes the brand read as `hinhhocvn`.
 *
 * The letters stay live text rather than becoming a path, so they inherit the loaded face, scale
 * with the user's font settings, and can be selected and read aloud.
 */
export function Wordmark({ size = 18, onDark = false }: WordmarkProps) {
  return (
    <span
      style={{
        font: `700 ${String(size)}px/1 var(--es-display)`,
        letterSpacing: '-0.012em',
        color: onDark ? '#ffffff' : 'var(--es-accent-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      h<span style={{ color: 'var(--es-apex)' }}>i</span>nhhoc
      <span
        style={{
          fontWeight: 400,
          color: onDark ? 'rgb(255 255 255 / 62%)' : 'var(--es-ink-400)',
        }}
      >
        .vn
      </span>
    </span>
  );
}

export interface LockupProps {
  readonly size?: number;
  readonly onDark?: boolean;
}

/** Mark and wordmark together, spaced by the wordmark's own `h`. */
export function Lockup({ size = 26, onDark = false }: LockupProps) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.34 }}>
      <Logo size={size} onDark={onDark} />
      <Wordmark size={size * 0.72} onDark={onDark} />
    </span>
  );
}
